import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { Button, Form, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { CartContext } from '../../context/CartContext';
import NavBar from './NavBar';
import './HomePageCard.css';

const HomePageCard = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [quantity, setQuantity] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const { addToCart } = useContext(CartContext);
  const tourRef = useRef(null);

  // Fetch menu items from the backend
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await axios.get('http://185.7.81.222:5000/api/menuitems');
        setMenuItems(response.data);
        
        // Initialize quantity for each menu item
        const initialQuantity = response.data.reduce((acc, item) => {
          acc[item._id] = 0;
          return acc;
        }, {});
        setQuantity(initialQuantity);
      } catch (error) {
        console.error('Error fetching menu items:', error);
      }
    };

    fetchMenuItems();
  }, []);

  // Initialize and start the Shepherd tour
  useEffect(() => {
    if (!tourRef.current) {
      tourRef.current = new Shepherd.Tour({
        defaultStepOptions: {
          classes: 'shadow-md bg-white rounded',
          scrollTo: { behavior: 'smooth', block: 'center' },
          modalOverlayOpeningPadding: 5,
          modalOverlayOpeningRadius: 5,
          cancelIcon: {
            enabled: true,
          },
        },
        useModalOverlay: true,
      });

      // Define tour steps
      const steps = [
        {
          id: 'menu-items',
          text: 'Here you can see all the menu items available.',
          attachTo: { element: '.card', on: 'top' },
          buttons: [
            {
              text: 'Next',
              action: tourRef.current.next,
            },
            {
              text: 'Skip',
              action: tourRef.current.complete,
            },
          ],
        },
        {
          id: 'quantity-control',
          text: 'Use these buttons to adjust the quantity of each item.',
          attachTo: { element: '.d-flex.align-items-center', on: 'top' },
          buttons: [
            {
              text: 'Next',
              action: tourRef.current.next,
            },
            {
              text: 'Skip',
              action: tourRef.current.complete,
            },
          ],
        },
        {
          id: 'add-to-cart',
          text: 'Click here to add an item to your cart.',
          attachTo: { element: '.btn-primary', on: 'top' },
          buttons: [
            {
              text: 'Next',
              action: tourRef.current.next,
            },
            {
              text: 'Skip',
              action: tourRef.current.complete,
            },
          ],
        },
        {
          id: 'place-order',
          text: 'Once you have added items to your cart, click the cart icon in the navbar to proceed with your order.',
          attachTo: { element: '.nav-link[href="/cart"]', on: 'bottom' }, // Adjusted for NavBar link to cart
          buttons: [
            {
              text: 'Next',
              action: tourRef.current.next,
            },
            {
              text: 'Skip',
              action: tourRef.current.complete,
            },
          ],
        },
        {
          id: 'finish',
          text: 'That’s it! You’re ready to explore our menu and place an order. Enjoy your time here!',
          buttons: [
            {
              text: 'Finish',
              action: tourRef.current.complete,
            },
          ],
        },
      ];

      // Add each step to the tour
      steps.forEach((step) => tourRef.current.addStep(step));
    }

    // Start the tour when the component mounts
    tourRef.current.start();

    // Cleanup on component unmount
    return () => {
      tourRef.current?.complete();
    };
  }, []);

  // Increase quantity for a specific menu item
  const handleIncreaseQuantity = (itemId) => {
    setQuantity((prevQuantity) => ({
      ...prevQuantity,
      [itemId]: prevQuantity[itemId] + 1,
    }));
  };

  // Decrease quantity for a specific menu item
  const handleDecreaseQuantity = (itemId) => {
    setQuantity((prevQuantity) => ({
      ...prevQuantity,
      [itemId]: prevQuantity[itemId] > 0 ? prevQuantity[itemId] - 1 : 0,
    }));
  };

  // Select an item to add to the cart
  const handleSelectItem = (item) => {
    setSelectedItem(item);
  };

  // Add the selected item to the cart with the selected option (oil or butter)
  const handleAddToCart = (option) => {
    if (quantity[selectedItem._id] > 0) {
      const cartItem = {
        id: selectedItem._id,
        name: selectedItem.itemName,
        price: selectedItem.itemPrice,
        quantity: quantity[selectedItem._id],
        option: option,
        imageUrl: `${selectedItem.itemImage}`,
      };
      addToCart(cartItem);
      setSelectedItem(null);
    }
  };

  return (
    <div>
      <NavBar title="La'RIVERA INN" />
      <Container>
        <Row>
          {menuItems.length === 0 ? (
            <p>No menu items available</p>
          ) : (
            menuItems.map((item) => (
              <Col key={item._id} sm={12} md={4} lg={4}>
                <Card className="mb-4">
                  <Card.Img variant="top" src={`${item.itemImage}`} />
                  <Card.Body>
                    <Card.Title>{item.itemName}</Card.Title>
                    <Card.Text>{item.description}</Card.Text>
                    <Card.Text>Price: ₹{item.itemPrice}</Card.Text>
                    <Form.Group controlId={`quantity-${item._id}`} className="mb-3">
                      <Form.Label>Quantity:</Form.Label>
                      <div className="d-flex align-items-center">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleDecreaseQuantity(item._id)}
                        >
                          -
                        </Button>
                        <Form.Control
                          type="number"
                          value={quantity[item._id]}
                          onChange={(e) =>
                            setQuantity({ ...quantity, [item._id]: parseInt(e.target.value) || 1 })
                          }
                          className="mx-2 text-center"
                          style={{ width: '50px' }}
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleIncreaseQuantity(item._id)}
                        >
                          +
                        </Button>
                      </div>
                    </Form.Group>
                    <Button
                      onClick={() => handleSelectItem(item)}
                      variant="primary"
                      className="w-100"
                    >
                      Add to Cart
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>

        {selectedItem && (
          <Modal show={true} onHide={() => setSelectedItem(null)}>
            <Modal.Header closeButton>
              <Modal.Title>Choose Option for {selectedItem.itemName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Would you like your {selectedItem.itemName} with oil or butter?</p>
              <div className="button-group">
                <Button onClick={() => handleAddToCart('oil')} variant="success">
                  With Oil
                </Button>
                <Button onClick={() => handleAddToCart('butter')} variant="success">
                  With Butter
                </Button>
                <Button onClick={() => setSelectedItem(null)} variant="secondary">
                  Cancel
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        )}
      </Container>
    </div>
  );
};

export default HomePageCard;
